import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, useContext, useMemo } from 'react';
import { useParams, useNavigate, useOutletContext, useSearchParams } from 'react-router-dom';
import { useVirtualizer } from '@tanstack/react-virtual';
import { chatsApi, ChatType } from '../../api/chats';
import { pollsApi } from '../../api/polls';
import { profilesApi } from '../../api/profiles';
import { filesApi } from '../../api/files';
import { presenceApi } from '../../api/presence';
import { UserContext } from '../../context/UserContext';
import { subscribeToPresence } from '../../utils/presenceSubscription';
import HrnbContainer from '../controls/HrnbContainer';
import HrnbAlert from '../controls/HrnbAlert';
import HrnbLoader from '../controls/HrnbLoader';
import HrnbButton from '../controls/HrnbButton';
import HrnbImage from '../controls/HrnbImage';
import OnlineStatusIndicator from '../controls/OnlineStatusIndicator';
import ChatMessage from './ChatMessage';
import SendMessageForm from './SendMessageForm';
import ForwardMessageModal from './ForwardMessageModal';
import ManageChatModal from './ManageChatModal';
import BookmarksPanel from './BookmarksPanel';
import PinnedMessageBanner from './PinnedMessageBanner';
import { requestNotificationPermission } from '../../utils/chatNotifications';
import { useAvatarUrl } from '../../hooks/useAvatarUrl';
import { toggleChatMute, useMutedChatIds } from '../../utils/chatMute';
import { useChatMessages } from './hooks/useChatMessages';
import { useChatWebSocket } from './hooks/useChatWebSocket';
import { useMessageSelection } from './hooks/useMessageSelection';
import { findMyReaction } from '../../utils/reactions';
import { buildVirtualItems } from './utils/buildVirtualItems';
import '../../css/chats/ChatWindow.css';

/**
 * Форматирование метки даты для разделителя в ленте сообщений.
 * Возвращает "Сегодня", "Вчера" или дату в формате "12 мая" (без года,
 * если текущий год; с годом — если другой).
 */
function formatDateSeparator(date) {
    const now = new Date();
    const msgDate = new Date(date);
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const msgDay = new Date(msgDate.getFullYear(), msgDate.getMonth(), msgDate.getDate());
    const diffDays = Math.round((today - msgDay) / 86400000);

    if (diffDays === 0) return 'Сегодня';
    if (diffDays === 1) return 'Вчера';

    const months = ['января', 'февраля', 'марта', 'апреля', 'мая', 'июня',
        'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря'];
    const sameYear = msgDate.getFullYear() === now.getFullYear();
    return sameYear
        ? `${msgDate.getDate()} ${months[msgDate.getMonth()]}`
        : `${msgDate.getDate()} ${months[msgDate.getMonth()]} ${msgDate.getFullYear()}`;
}

/**
 * Окно чата с сообщениями
 * Интеграция: getMessages, sendMessage (content вместо text)
 *
 * Логика разделена на custom hooks:
 *  - useChatMessages — загрузка, пагинация, sync, polling
 *  - useChatWebSocket — подписки WS, typing, join/leave, уведомления
 *  - useMessageSelection — режим выбора, копирование/удаление/пересылка
 */
export default function ChatWindow() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const targetMessageId = searchParams.get('msg');
    const { currentUser } = useContext(UserContext);

    // Refs для скролла и DOM
    const messagesEndRef = useRef(null);
    const messagesContainerRef = useRef(null);
    const topSentinelRef = useRef(null);

    // Универсальный ID профиля текущего пользователя
    const currentProfileId = currentUser?.profileId || currentUser?.profiles?.[0]?.id;

    // wsConnected приходит из ChatsLayout через Outlet context
    const { wsConnected = false, refreshChats, updateChatLocally } = useOutletContext() || {};
    const refreshChatsRef = useRef(refreshChats);
    refreshChatsRef.current = refreshChats;
    const updateChatLocallyRef = useRef(updateChatLocally);
    updateChatLocallyRef.current = updateChatLocally;

    // Локальное UI-состояние (не вошедшее в hooks)
    const [sending, setSending] = useState(false);
    const [otherParticipantPresence, setOtherParticipantPresence] = useState({ isOnline: false, lastActivity: null });
    const [otherProfileInfo, setOtherProfileInfo] = useState(null);
    const [replyToMessage, setReplyToMessage] = useState(null);
    const [editingMessage, setEditingMessage] = useState(null);
    const [showScrollButton, setShowScrollButton] = useState(false);
    const [newMessagesCount, setNewMessagesCount] = useState(0);
    const [showManageModal, setShowManageModal] = useState(false);
    const [participantProfiles, setParticipantProfiles] = useState([]);
    const [pinnedMessages, setPinnedMessages] = useState([]);
    const isAtBottomRef = useRef(true);

    // Поиск по сообщениям внутри чата
    const [searchMode, setSearchMode] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [currentSearchIndex, setCurrentSearchIndex] = useState(-1);
    const [loadingAllForSearch, setLoadingAllForSearch] = useState(false);

    // === Hook: загрузка/пагинация/sync сообщений ===
    const {
        chat, setChat,
        messages, setMessages,
        loading,
        error, setError,
        chatParticipants,
        hasMoreMessages,
        loadingMore,
        messagesPage, setMessagesPage,
        setHasMoreMessages,
        abortControllerRef,
        pendingMessagesRef,
        totalPagesRef,
        isFirstLoadRef,
        scrollRestoreRef,
        loadingMoreRef,
        firstUnreadMessageIdRef,
        lastMessageCountRef,
        isMessageRead,
        syncMessages,
    } = useChatMessages({ id, currentProfileId, wsConnected, messagesContainerRef, topSentinelRef });

    // === Виртуализация списка сообщений (@tanstack/react-virtual) ===
    // Плоский массив элементов: сообщения + разделители дат/непрочитанных.
    // Только видимые элементы рендерятся в DOM — это решает тормоза при 1000+ сообщений.
    const { items, messageIdToItemIndex } = useMemo(
        () => buildVirtualItems({
            messages,
            currentProfileId,
            isMessageRead,
            searchResults,
            currentSearchIndex,
            searchMode,
            searchQuery,
        }),
        [messages, currentProfileId, isMessageRead, searchResults, currentSearchIndex, searchMode, searchQuery]
    );

    // Refs для доступа из стабильных callback'ов (scrollToBottom/scrollToMessage)
    const virtualizerRef = useRef(null);
    const itemsRef = useRef(items);
    const messageIdToItemIndexRef = useRef(messageIdToItemIndex);
    itemsRef.current = items;
    messageIdToItemIndexRef.current = messageIdToItemIndex;
    const messagesRef = useRef(messages);
    messagesRef.current = messages;

    const estimateItemSize = useCallback((index) => {
        const item = itemsRef.current[index];
        if (!item) return 80;
        if (item.type === 'date-separator' || item.type === 'unread-separator') return 45;
        return 80;
    }, []);

    const virtualizer = useVirtualizer({
        count: items.length,
        getScrollElement: () => messagesContainerRef.current,
        estimateSize: estimateItemSize,
        overscan: 10,
        getItemKey: useCallback((index) => itemsRef.current[index]?.key ?? index, []),
    });
    virtualizerRef.current = virtualizer;

    // === Скролл-функции (зависят от refs, объявленных в компоненте) ===

    const scrollToBottom = useCallback(() => {
        const len = itemsRef.current.length;
        if (len > 0) {
            virtualizerRef.current?.scrollToIndex(len - 1, { align: 'end' });
        }
        isAtBottomRef.current = true;
        setShowScrollButton(false);
        setNewMessagesCount(0);
    }, []);

    const scrollToMessage = useCallback((messageId) => {
        if (!messageId) return;
        const idx = messageIdToItemIndexRef.current[messageId];
        if (idx !== undefined) {
            virtualizerRef.current?.scrollToIndex(idx, { align: 'start' });
        } else {
            // Fallback: прокрутка в конец
            const len = itemsRef.current.length;
            if (len > 0) {
                virtualizerRef.current?.scrollToIndex(len - 1, { align: 'end' });
            }
        }
    }, []);

    // === Hook: WebSocket-подписки ===
    const { typingUsers, sendMessageWs, sendTypingWs, sendStopTypingWs } = useChatWebSocket({
        id,
        currentUser,
        wsConnected,
        currentProfileId,
        setMessages,
        pendingMessagesRef,
        lastMessageCountRef,
        scrollToBottom,
        isAtBottomRef,
        setNewMessagesCount,
        setError,
        // Локально обновляем превью чата в сайдбаре при получении нового сообщения.
        // unread не увеличиваем — пользователь уже в этом чате (сообщения читаются).
        onNewMessage: useCallback((message) => {
            updateChatLocallyRef.current?.(id, message, false);
        }, [id]),
        // Real-time обновление закреплённых сообщений через WS
        onPinnedUpdate: useCallback((pinnedEvent) => {
            if (!pinnedEvent) return;
            const { type, message: pinnedMessage } = pinnedEvent;
            if (type === 'PIN' && pinnedMessage) {
                setPinnedMessages(prev =>
                    prev.some(m => m.id === pinnedMessage.id)
                        ? prev
                        : [...prev, { ...pinnedMessage, isPinned: true }]
                );
                setMessages(prev => prev.map(m =>
                    m.id === pinnedMessage.id ? { ...m, isPinned: true } : m
                ));
            } else if (type === 'UNPIN' && pinnedMessage) {
                setPinnedMessages(prev => prev.filter(m => m.id !== pinnedMessage.id));
                setMessages(prev => prev.map(m =>
                    m.id === pinnedMessage.id ? { ...m, isPinned: false } : m
                ));
            }
        }, [id]),
        // Real-time обновление опросов через WS (голосование, закрытие)
        onPollUpdate: useCallback((pollEvent) => {
            if (!pollEvent) return;
            // Обновляем кэш опросов
            pollsCacheRef.current = pollsCacheRef.current.map(p =>
                p.id === pollEvent.id ? pollEvent : p
            );
            // pollEvent — это обновлённый PollResponse
            // Обновляем poll внутри сообщения, к которому привязан опрос.
            // Проверяем по pollId/poll.id — не фильтруем по type,
            // т.к. сервер может не вернуть type: 'POLL' после sync.
            setMessages(prev => prev.map(m => {
                const msgPollId = m.pollId || m.poll?.id;
                if (msgPollId === pollEvent.id) {
                    return { ...m, type: 'POLL', poll: pollEvent };
                }
                return m;
            }));
        }, [id]),
    });

    // === Hook: режим выбора сообщений ===
    const {
        selectionMode, setSelectionMode,
        selectedMessageIds,
        showForwardModal, setShowForwardModal,
        setSingleForwardMessage,
        actionLoading,
        allSelected,
        enterSelectionMode,
        exitSelectionMode,
        toggleSelectAll,
        toggleMessageSelection,
        handleDeleteSelected,
        handleCopySelected,
        handleForwardSelected,
        handleForwardSingle,
    } = useMessageSelection({ id, messages, setMessages, currentProfileId, setError, onForwarded: (targetChatId) => navigate(`/chats/${targetChatId}`) });

    // === Mute-статус чата ===
    const mutedChatIds = useMutedChatIds();
    const isMuted = mutedChatIds.has(String(id));
    const handleToggleMute = useCallback(() => {
        // Локальный mute (звук уведомлений в браузере)
        const nowMuted = toggleChatMute(id);
        // Серверный mute (исключение из push-уведомлений)
        // Ошибки сервера не блокируем локальный mute — пользователь
        // всё равно не услышит звук, а сервер ретраит через refresh
        const apiCall = nowMuted
            ? chatsApi.muteChat(id)
            : chatsApi.unmuteChat(id);
        apiCall.catch(err => {
            console.error('[ChatWindow] Server mute/unmute failed:', err.message);
        });
    }, [id]);

    // Обновление локального объекта чата после управления (смена названия, участники и т.д.)
    const handleChatUpdated = useCallback((updatedChat) => {
        if (updatedChat?.id === id) {
            setChat(updatedChat);
            // Если после обновления lastMessageAt стал null — история очищена
            if (!updatedChat.lastMessageAt) {
                setMessages([]);
            }
            // Обновляем список чатов в сайдбаре (через ref, чтобы избежать лишних пересозданий callback)
            refreshChatsRef.current?.();
        }
    }, [id, setChat, setMessages]);

    // === Локальные эффекты (UI-связанные, не вошедшие в hooks) ===

    // Запрос разрешения на браузерные уведомления при первом входе в чат
    useEffect(() => {
        if (currentUser) {
            requestNotificationPermission();
        }
    }, [currentUser]);

    // Сброс локального UI-состояния при смене чата
    useEffect(() => {
        setOtherProfileInfo(null);
        setOtherParticipantPresence({ isOnline: false, lastActivity: null });
        setReplyToMessage(null);
        setEditingMessage(null);
        setSending(false);
        setShowScrollButton(false);
        setNewMessagesCount(0);
        setPinnedMessages([]);
        isAtBottomRef.current = true;
        // Сброс кэша опросов
        pollsCacheRef.current = [];
        setPollsVersion(0);
        setSearchMode(false);
        setSearchQuery('');
        setSearchResults([]);
        setCurrentSearchIndex(-1);
        setLoadingAllForSearch(false);
        setShowManageModal(false);
    }, [id]);

    // Прокрутка к целевому сообщению (из глобального поиска), первому непрочитанному или к низу
    useLayoutEffect(() => {
        if (isFirstLoadRef.current && messages.length > 0 && !loadingMoreRef.current) {
            isFirstLoadRef.current = false;
            if (targetMessageId) {
                scrollToMessage(targetMessageId);
                // Очищаем URL-параметр после прокрутки
                searchParams.delete('msg');
                setSearchParams(searchParams, { replace: true });
            } else if (firstUnreadMessageIdRef.current) {
                scrollToMessage(firstUnreadMessageIdRef.current);
            } else {
                scrollToBottom();
            }
        }
    }, [messages, isFirstLoadRef, firstUnreadMessageIdRef, loadingMoreRef, scrollToMessage, scrollToBottom, targetMessageId, searchParams, setSearchParams]);

    // Восстановление позиции скролла после подгрузки старых сообщений
    useLayoutEffect(() => {
        if (scrollRestoreRef.current && messagesContainerRef.current) {
            const { prevScrollHeight, prevScrollTop } = scrollRestoreRef.current;
            const newScrollHeight = messagesContainerRef.current.scrollHeight;
            messagesContainerRef.current.scrollTop = newScrollHeight - prevScrollHeight + prevScrollTop;
            scrollRestoreRef.current = null;
        }
    }, [messages, scrollRestoreRef, messagesContainerRef]);

    // Загрузка профиля и онлайн-статуса собеседника для DIRECT чатов
    useEffect(() => {
        if (!chat || chat.chatType !== ChatType.DIRECT || !currentUser) return;
        const participants = chat.participantIds || chat.participants || chatParticipants || [];
        if (participants.length === 0) return;

        let unsubscribePresence = null;

        const fetchOtherParticipant = async () => {
            try {
                const otherParticipantId = participants.find(pId => pId !== currentProfileId) || participants[0];
                if (!otherParticipantId) return;

                const profileResponse = await profilesApi.get(otherParticipantId);
                const profileData = profileResponse.data || profileResponse;
                setOtherProfileInfo(profileData);

                const otherUserId = profileData.userId;
                if (!otherUserId) return;

                const presence = await presenceApi.getStatus(otherUserId);
                setOtherParticipantPresence({ isOnline: presence.isOnline, lastActivity: presence.lastActivity });

                unsubscribePresence = subscribeToPresence(otherUserId, (data) => {
                    setOtherParticipantPresence({ isOnline: data.isOnline, lastActivity: data.lastActivity });
                });
            } catch (err) {
                console.error('[ChatWindow] Failed to fetch participant info:', err);
            }
        };

        fetchOtherParticipant();

        return () => {
            if (unsubscribePresence) unsubscribePresence.unsubscribe();
        };
    }, [chat, chatParticipants, currentUser, currentProfileId]);

    // Загрузка профилей участников для @mentions (групповые чаты)
    useEffect(() => {
        if (!chat || chat.chatType === ChatType.DIRECT) {
            setParticipantProfiles([]);
            return;
        }
        const participantIds = chat.participantIds || chat.participants || chatParticipants || [];
        if (participantIds.length === 0) {
            setParticipantProfiles([]);
            return;
        }

        let cancelled = false;
        const loadProfiles = async () => {
            try {
                const profiles = await Promise.all(
                    participantIds.map(async (pid) => {
                        try {
                            const resp = await profilesApi.get(pid);
                            const p = resp?.data || resp;
                            return {
                                id: pid,
                                displayName: p.displayName || p.name || 'Участник',
                                avatarUrl: p.avatarUrl || null,
                                avatar: p.avatar || null,
                            };
                        } catch {
                            return { id: pid, displayName: 'Участник', avatarUrl: null, avatar: null };
                        }
                    })
                );
                if (!cancelled) setParticipantProfiles(profiles);
            } catch (err) {
                console.warn('[ChatWindow] Failed to load participant profiles for mentions:', err.message);
            }
        };
        loadProfiles();
        return () => { cancelled = true; };
    }, [chat, chatParticipants]);

    // Загрузка закреплённых сообщений при открытии чата
    useEffect(() => {
        if (!chat) {
            setPinnedMessages([]);
            return;
        }
        let cancelled = false;
        const loadPinned = async () => {
            try {
                const pinned = await chatsApi.getPinnedMessages(id);
                if (!cancelled) setPinnedMessages(pinned);
            } catch (err) {
                console.warn('[ChatWindow] Failed to load pinned messages:', err.message);
                if (!cancelled) setPinnedMessages([]);
            }
        };
        loadPinned();
        return () => { cancelled = true; };
    }, [id, chat]);

    // ═══════════════════════════════════════════
    //  Опросы: кэш + матчинг с сообщениями
    // ═══════════════════════════════════════════
    //
    // Сервер НЕ возвращает type: 'POLL' в MessageResponse — тип нужно
    // восстанавливать на клиенте, матча сообщения с опросами по messageId.
    //
    // Архитектура:
    // 1. pollsCacheRef — кэш всех опросов чата (загружается при открытии)
    // 2. pollsVersion — счётчик для триггера useEffect при обновлении кэша
    // 3. Эффект матчинга — запускается при изменении messages.length или
    //    pollsVersion, находит сообщения без type:'POLL' и сопоставляет с кэшем

    const pollsCacheRef = useRef([]);
    const [pollsVersion, setPollsVersion] = useState(0);

    // Загрузка опросов при открытии чата + периодическое обновление (только без WS)
    useEffect(() => {
        if (!chat || !id) return;
        let cancelled = false;
        const loadPolls = async () => {
            try {
                const polls = await pollsApi.getAll(id);
                if (cancelled) return;
                const prev = pollsCacheRef.current;
                // Обновляем кэш только если данные изменились
                const changed = prev.length !== polls.length
                    || polls.some(p => {
                        const old = prev.find(o => o.id === p.id);
                        return !old
                            || old.voteCount !== p.voteCount
                            || old.isClosed !== p.isClosed
                            || JSON.stringify(old.options) !== JSON.stringify(p.options);
                    });
                if (changed) {
                    pollsCacheRef.current = polls || [];
                    setPollsVersion(v => v + 1);
                }
            } catch (err) {
                // ignore — некритично
            }
        };
        pollsCacheRef.current = [];
        loadPolls();
        // Polling только при отсутствии WebSocket — обновления опросов
        // приходят через WS-подписку subscribeToPolls
        let interval = null;
        if (!wsConnected) {
            interval = setInterval(loadPolls, 15000);
        }
        return () => {
            cancelled = true;
            if (interval) clearInterval(interval);
        };
    }, [id, chat, wsConnected]);

    // Матчинг опросов с сообщениями — запускается при изменении
    // количества сообщений (новые/удалённые) или при обновлении кэша опросов.
    // Идемпотентен: сообщения с уже установленным type:'POLL' пропускаются.
    useEffect(() => {
        const polls = pollsCacheRef.current;
        if (polls.length === 0 || messages.length === 0) return;

        setMessages(prev => {
            let changed = false;
            const next = prev.map(m => {
                // Уже сопоставлено — пропускаем
                if (m.type === 'POLL' && m.poll) return m;
                const matched = polls.find(p =>
                    p.id === m.pollId || p.id === m.poll?.id || p.messageId === m.id
                );
                if (matched) {
                    changed = true;
                    return { ...m, type: 'POLL', pollId: matched.id, poll: matched };
                }
                return m;
            });
            return changed ? next : prev;
        });
    }, [messages.length, pollsVersion, setMessages]);

    // Обработчики закрепления/открепления
    const handlePinMessage = useCallback(async (message) => {
        try {
            await chatsApi.pinMessage(id, message.id);
            setPinnedMessages(prev => {
                if (prev.some(m => m.id === message.id)) return prev;
                return [...prev, { ...message, isPinned: true }];
            });
        } catch (err) {
            console.error('[ChatWindow] Failed to pin message:', err);
            setError(err.response?.data?.message || 'Не удалось закрепить сообщение');
        }
    }, [id, setError]);

    const handleUnpinMessage = useCallback(async (message) => {
        try {
            await chatsApi.unpinMessage(id, message.id);
            setPinnedMessages(prev => prev.filter(m => m.id !== message.id));
            // Обновляем флаг isPinned в списке сообщений
            setMessages(prev => prev.map(m =>
                m.id === message.id ? { ...m, isPinned: false } : m
            ));
        } catch (err) {
            console.error('[ChatWindow] Failed to unpin message:', err);
            setError(err.response?.data?.message || 'Не удалось открепить сообщение');
        }
    }, [id, setMessages, setError]);

    // Прокрутка к закреплённому сообщению
    const scrollToPinnedMessage = useCallback((messageId) => {
        const container = messagesContainerRef.current;
        if (!container) return;
        const el = container.querySelector(`[data-message-id="${messageId}"]`);
        if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            el.classList.add('chat-message--pinned-highlight');
            setTimeout(() => el.classList.remove('chat-message--pinned-highlight'), 2000);
        }
    }, [messagesContainerRef]);

    // ═══════════════════════════════════════════
    //  Закладки (Bookmarks)
    // ═══════════════════════════════════════════

    const [bookmarkedMessageIds, setBookmarkedMessageIds] = useState(new Set());
    const [showBookmarksPanel, setShowBookmarksPanel] = useState(false);

    // Обработчик закладки: добавить/удалить
    const handleToggleBookmark = useCallback(async (message, shouldBookmark) => {
        if (!id || !message?.id) return;
        // Оптимистичное обновление
        setBookmarkedMessageIds(prev => {
            const next = new Set(prev);
            if (shouldBookmark) next.add(message.id);
            else next.delete(message.id);
            return next;
        });
        try {
            if (shouldBookmark) {
                await chatsApi.addBookmark(id, message.id);
            } else {
                await chatsApi.removeBookmark(id, message.id);
            }
        } catch (err) {
            // Откатываем при ошибке
            setBookmarkedMessageIds(prev => {
                const next = new Set(prev);
                if (shouldBookmark) next.delete(message.id);
                else next.add(message.id);
                return next;
            });
            console.error('[ChatWindow] Failed to toggle bookmark:', err);
            setError(err.response?.data?.message || 'Не удалось изменить закладку');
        }
    }, [id, setError]);

    // Обработчик скролла: отслеживаем позицию «внизу» для плавающей кнопки
    const handleScroll = useCallback(() => {
        const container = messagesContainerRef.current;
        if (!container) return;

        if (scrollRestoreRef.current || loadingMoreRef.current) return;

        const threshold = 120;
        const isAtBottom = container.scrollHeight - container.scrollTop - container.clientHeight < threshold;
        isAtBottomRef.current = isAtBottom;

        if (isAtBottom) {
            setShowScrollButton(false);
            setNewMessagesCount(0);
        } else {
            setShowScrollButton(true);
        }
    }, [scrollRestoreRef, loadingMoreRef]);

    const handleScrollToBottomClick = useCallback(() => {
        scrollToBottom();
        setNewMessagesCount(0);
        setShowScrollButton(false);
        isAtBottomRef.current = true;
    }, [scrollToBottom]);

    // === Поиск по сообщениям внутри чата ===

    useEffect(() => {
        if (!searchMode || !searchQuery.trim()) {
            setSearchResults([]);
            setCurrentSearchIndex(-1);
            return;
        }
        const query = searchQuery.toLowerCase();
        const results = messages
            .filter(m => !m.isDeleted && !m._pending)
            .map(m => ({ id: m.id, content: (m.content || m.text || '').toLowerCase() }))
            .filter(m => m.content.includes(query))
            .map(m => m.id);
        setSearchResults(results);
        setCurrentSearchIndex(results.length > 0 ? 0 : -1);
    }, [searchQuery, searchMode, messages]);

    useEffect(() => {
        if (currentSearchIndex >= 0 && searchResults[currentSearchIndex]) {
            scrollToMessage(searchResults[currentSearchIndex]);
        }
    }, [currentSearchIndex, searchResults, scrollToMessage]);

    const handleSearchPrev = useCallback(() => {
        if (searchResults.length === 0) return;
        setCurrentSearchIndex(prev => prev <= 0 ? searchResults.length - 1 : prev - 1);
    }, [searchResults]);

    const handleSearchNext = useCallback(() => {
        if (searchResults.length === 0) return;
        setCurrentSearchIndex(prev => prev >= searchResults.length - 1 ? 0 : prev + 1);
    }, [searchResults]);

    const handleExitSearch = useCallback(() => {
        setSearchMode(false);
        setSearchQuery('');
        setSearchResults([]);
        setCurrentSearchIndex(-1);
    }, []);

    useEffect(() => {
        if (!searchMode) return;
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') {
                handleExitSearch();
            } else if (e.key === 'Enter' && searchResults.length > 0) {
                if (e.shiftKey) handleSearchPrev();
                else handleSearchNext();
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [searchMode, searchResults, handleExitSearch, handleSearchPrev, handleSearchNext]);

    // Esc — выход из чата на мобильных (когда нет активного поиска/выбора)
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key !== 'Escape') return;
            if (searchMode || selectionMode) return; // эти режимы обрабатывают Esc сами
            if (window.innerWidth > 768) return;     // только на мобильных
            navigate('/chats');
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [searchMode, selectionMode, navigate]);

    const loadAllMessagesForSearch = useCallback(async () => {
        if (!hasMoreMessages && messagesPage < 0) return;
        setLoadingAllForSearch(true);
        try {
            const totalPages = totalPagesRef.current;
            const signal = abortControllerRef.current?.signal;

            // Параллельная загрузка всех страниц через Promise.all
            const pageNumbers = [];
            for (let p = 0; p <= totalPages - 1; p++) {
                pageNumbers.push(p);
            }

            const responses = await Promise.all(
                pageNumbers.map(p =>
                    chatsApi.getMessages(
                        id,
                        { page: p, size: 50, sortBy: 'createdAt', direction: 'DESC' },
                        signal
                    ).catch(err => {
                        // Не роняем весь Promise.all при ошибке одной страницы
                        if (err.name === 'AbortError' || err.code === 'ERR_CANCELED') throw err;
                        console.warn(`[ChatWindow] Page ${p} load failed:`, err.message);
                        return null;
                    })
                )
            );

            const allPages = [];
            for (const response of responses) {
                if (!response) continue;
                let pageMessages = [];
                if (Array.isArray(response)) {
                    pageMessages = response;
                } else if (response && typeof response === 'object') {
                    pageMessages = response.content || response.data || response.messages || [];
                }
                allPages.push(...pageMessages);
            }

            const unique = allPages.filter((msg, index, self) =>
                index === self.findIndex((m) => m.id === msg.id)
            ).sort((a, b) =>
                new Date(a.createdAt || a.sentAt) - new Date(b.createdAt || b.sentAt)
            );

            setMessages(unique);
            setMessagesPage(-1);
            setHasMoreMessages(false);
        } catch (err) {
            if (err.name !== 'AbortError' && err.code !== 'ERR_CANCELED') {
                console.error('[ChatWindow] Ошибка загрузки всех сообщений для поиска:', err);
                setError('Не удалось загрузить всю историю');
            }
        } finally {
            setLoadingAllForSearch(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, messagesPage, totalPagesRef, abortControllerRef, setMessages, setMessagesPage, setHasMoreMessages, setError]);

    // === Отправка/редактирование/удаление/реакции (UI-обработчики) ===

    const createOptimisticMessage = useCallback((text, replyToId = null) => {
        const tempId = `optimistic-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        const now = new Date().toISOString();
        const displayName = currentUser?.profiles?.[0]?.displayName
            || currentUser?.displayName
            || currentUser?.username
            || 'Я';
        const avatar = currentUser?.profiles?.[0]?.avatar
            ? `/api/files/${currentUser.profiles[0].avatar}`
            : '/default-avatar.png';

        return {
            id: tempId,
            content: text,
            text: text,
            createdAt: now,
            sentAt: now,
            sender: { id: currentProfileId, displayName, avatar },
            senderId: currentProfileId,
            senderDisplayName: displayName,
            attachments: [],
            invitationAttachments: [],
            reactions: [],
            isDeleted: false,
            _pending: true,
            replyToMessageId: replyToId || null,
        };
    }, [currentUser, currentProfileId]);

    const sendViaRest = useCallback(async (text, attachments, optimisticTempId = null) => {
        setSending(true);
        try {
            const messagePayload = {
                senderId: currentProfileId || null,
                chatId: id,
                content: text || '',
                attachmentIds: attachments
            };

            const response = await chatsApi.sendMessage(id, messagePayload);

            if (response) {
                setMessages(prev => {
                    const withoutPending = optimisticTempId
                        ? prev.filter(m => m.id !== optimisticTempId)
                        : prev;
                    const exists = withoutPending.find(m => m.id === response.id);
                    if (exists) {
                        return withoutPending.map(m => m.id === response.id ? { ...m, ...response } : m);
                    }
                    return [...withoutPending, response].sort((a, b) =>
                        new Date(a.createdAt || a.sentAt) - new Date(b.createdAt || b.sentAt)
                    );
                });
                setTimeout(scrollToBottom, 100);
                // Локально обновляем превью чата в сайдбаре (без API-запроса)
                updateChatLocallyRef.current?.(id, response, false);
            }
            if (optimisticTempId) {
                pendingMessagesRef.current = pendingMessagesRef.current.filter(p => p.tempId !== optimisticTempId);
            }
        } catch (err) {
            if (optimisticTempId) {
                setMessages(prev => prev.filter(m => m.id !== optimisticTempId));
                pendingMessagesRef.current = pendingMessagesRef.current.filter(p => p.tempId !== optimisticTempId);
            }
            console.error('[ChatWindow] Ошибка отправки:', err);
            setError(err.message || 'Ошибка при отправке сообщения');
        } finally {
            setSending(false);
        }
    }, [id, currentProfileId, setMessages, pendingMessagesRef, scrollToBottom, setError]);

    const handleEditMessage = useCallback(async (text) => {
        setSending(true);
        try {
            const updated = await chatsApi.editMessage(editingMessage.id, text || '');
            setMessages(prev => {
                const next = prev.map(m => m.id === updated.id ? updated : m);
                // Если отредактировано последнее сообщение — обновляем превью чата
                const lastMsg = next[next.length - 1];
                if (lastMsg && lastMsg.id === updated.id) {
                    updateChatLocallyRef.current?.(id, updated, false);
                }
                return next;
            });
            setEditingMessage(null);
        } catch (err) {
            console.error('[ChatWindow] Ошибка редактирования:', err);
            setError(err.message || 'Ошибка при редактировании сообщения');
        } finally {
            setSending(false);
        }
    }, [editingMessage, setMessages, setEditingMessage, id, setError]);

    const sendWithAttachments = useCallback(async (text, attachmentIds) => {
        setSending(true);
        const optimistic = createOptimisticMessage(text || '', replyToMessage?.id);
        pendingMessagesRef.current.push({ tempId: optimistic.id, content: text || '' });
        setMessages(prev => [...prev, optimistic].sort((a, b) =>
            new Date(a.createdAt || a.sentAt) - new Date(b.createdAt || b.sentAt)
        ));
        setTimeout(scrollToBottom, 50);

        try {
            const messagePayload = {
                senderId: currentProfileId || null,
                chatId: id,
                content: text || '',
                attachmentIds: attachmentIds
            };
            if (replyToMessage?.id) {
                messagePayload.replyToMessageId = replyToMessage.id;
            }

            const response = await chatsApi.sendMessage(id, messagePayload);

            if (response) {
                setMessages(prev => {
                    const withoutPending = prev.filter(m => m.id !== optimistic.id);
                    const exists = withoutPending.find(m => m.id === response.id);
                    if (exists) {
                        return withoutPending.map(m => m.id === response.id ? { ...m, ...response } : m);
                    }
                    return [...withoutPending, response].sort((a, b) =>
                        new Date(a.createdAt || a.sentAt) - new Date(b.createdAt || b.sentAt)
                    );
                });
                setTimeout(scrollToBottom, 100);
                // Локально обновляем превью чата в сайдбаре (без API-запроса)
                updateChatLocallyRef.current?.(id, response, false);
            }
            pendingMessagesRef.current = pendingMessagesRef.current.filter(p => p.tempId !== optimistic.id);
            setReplyToMessage(null);
        } catch (err) {
            setMessages(prev => prev.filter(m => m.id !== optimistic.id));
            pendingMessagesRef.current = pendingMessagesRef.current.filter(p => p.tempId !== optimistic.id);
            console.error('[ChatWindow] Ошибка отправки:', err);
            setError(err.message || 'Ошибка при отправке сообщения');
        } finally {
            setSending(false);
        }
    }, [createOptimisticMessage, replyToMessage, pendingMessagesRef, setMessages, scrollToBottom, currentProfileId, id, setReplyToMessage, setError]);

    const sendTextMessage = useCallback(async (text) => {
        if (wsConnected) {
            const optimistic = createOptimisticMessage(text, replyToMessage?.id);
            pendingMessagesRef.current.push({ tempId: optimistic.id, content: text });
            setMessages(prev => [...prev, optimistic].sort((a, b) =>
                new Date(a.createdAt || a.sentAt) - new Date(b.createdAt || b.sentAt)
            ));
            setTimeout(scrollToBottom, 50);

            // Локально обновляем превью чата в сайдбаре (оптимистично, без API-запроса)
            updateChatLocallyRef.current?.(id, optimistic, false);

            const sent = sendMessageWs(text);
            if (!sent) {
                await sendViaRest(text, [], optimistic.id);
            } else {
                // WS отправлено успешно. Эхо-сообщение от сервера заменит
                // optimistic-сообщение через подписку subscribeToChat — дополнительный
                // REST-запрос syncMessages не нужен.
                // Если эхо не пришло в течение 5с — делаем fallback sync.
                setTimeout(() => syncMessages(), 5000);
            }
        } else {
            await sendViaRest(text, []);
        }
    }, [wsConnected, createOptimisticMessage, replyToMessage, pendingMessagesRef, setMessages, scrollToBottom, id, sendMessageWs, sendViaRest, syncMessages]);

    const handleSendMessage = useCallback(async (text, attachmentIds = []) => {
        const hasText = text && text.trim().length > 0;
        const hasAttachments = attachmentIds && attachmentIds.length > 0;

        if (!hasText && !hasAttachments) return;
        if (!currentUser) {
            setError('Загрузка пользователя...');
            return;
        }

        // V2: если редактируем — вызываем editMessage вместо отправки нового
        if (editingMessage) {
            await handleEditMessage(text);
            return;
        }

        // Если есть вложения или ответ — только REST (WebSocket не поддерживает файлы)
        if (hasAttachments || replyToMessage) {
            await sendWithAttachments(text, attachmentIds);
            return;
        }

        // Текстовое сообщение — WebSocket с fallback на REST
        await sendTextMessage(text);
    }, [currentUser, editingMessage, replyToMessage, handleEditMessage, sendWithAttachments, sendTextMessage, setError]);

    /**
     * Отправка стикера — всегда через REST.
     * Для локальных стикеров (imageFileId начинается с '/') отправляем
     * только stickerId — сервер сам найдёт файл. Для серверных стикеров
     * передаём attachmentIds.
     * @param {Object} sticker - { id, imageFileId, emoji? }
     */
    const handleSendSticker = useCallback(async (sticker) => {
        if (!sticker || !currentUser || !id) return;

        const stickerFileId = sticker.imageFileId || sticker.fileId;
        if (!stickerFileId) return;

        // Локальный стикер (SVG из public/) — нет fileId для сервера
        const isLocal = typeof stickerFileId === 'string' && stickerFileId.startsWith('/');

        setSending(true);

        // Оптимистичное сообщение-стикер
        const tempId = `optimistic-sticker-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        const now = new Date().toISOString();
        const displayName = currentUser?.profiles?.[0]?.displayName
            || currentUser?.displayName
            || currentUser?.username
            || 'Я';
        const avatar = currentUser?.profiles?.[0]?.avatar
            ? `/api/files/${currentUser.profiles[0].avatar}`
            : '/default-avatar.png';

        const optimistic = {
            id: tempId,
            content: '',
            text: '',
            type: 'STICKER',
            stickerId: sticker.id,
            createdAt: now,
            sentAt: now,
            sender: { id: currentProfileId, displayName, avatar },
            senderId: currentProfileId,
            senderDisplayName: displayName,
            // Для локальных стикеров сохраняем путь — StickerImage поймёт
            attachments: [stickerFileId],
            attachmentIds: [stickerFileId],
            invitationAttachments: [],
            reactions: [],
            isDeleted: false,
            _pending: true,
            replyToMessageId: replyToMessage?.id || null,
        };

        pendingMessagesRef.current.push({ tempId, content: '' });
        setMessages(prev => [...prev, optimistic].sort((a, b) =>
            new Date(a.createdAt || a.sentAt) - new Date(b.createdAt || b.sentAt)
        ));
        setTimeout(scrollToBottom, 50);

        try {
            let serverAttachmentIds = [];

            // Для локального стикера — сначала загружаем SVG на сервер
            if (isLocal) {
                const svgResponse = await fetch(stickerFileId);
                if (!svgResponse.ok) throw new Error('Не удалось прочитать локальный стикер');
                const svgBlob = await svgResponse.blob();
                const file = new File([svgBlob], `sticker-${sticker.id}.svg`, { type: 'image/svg+xml' });
                const uploadResult = await filesApi.upload(file, { type: 'IMAGE', entityType: 'CHAT' });
                const fileData = uploadResult?.file || uploadResult;
                const uploadedFileId = fileData?.id;
                if (uploadedFileId) {
                    serverAttachmentIds = [uploadedFileId];
                    // Обновляем оптимистичное сообщение — подставляем реальный fileId
                    setMessages(prev => prev.map(m =>
                        m.id === tempId
                            ? { ...m, attachments: [{ fileId: uploadedFileId, type: 'IMAGE', mimeType: 'image/svg+xml' }], attachmentIds: [uploadedFileId] }
                            : m
                    ));
                }
            } else {
                serverAttachmentIds = [stickerFileId];
            }

            const messagePayload = {
                senderId: currentProfileId || null,
                chatId: id,
                content: '',
                attachmentIds: serverAttachmentIds,
                stickerId: isLocal ? undefined : sticker.id,
            };
            // type STICKER передаём только для серверных стикеров
            if (!isLocal) {
                messagePayload.type = 'STICKER';
            }
            if (replyToMessage?.id) {
                messagePayload.replyToMessageId = replyToMessage.id;
            }

            const response = await chatsApi.sendMessage(id, messagePayload);

            if (response) {
                setMessages(prev => {
                    const withoutPending = prev.filter(m => m.id !== tempId);
                    const exists = withoutPending.find(m => m.id === response.id);
                    if (exists) {
                        return withoutPending.map(m => m.id === response.id ? { ...m, ...response } : m);
                    }
                    return [...withoutPending, response].sort((a, b) =>
                        new Date(a.createdAt || a.sentAt) - new Date(b.createdAt || b.sentAt)
                    );
                });
                setTimeout(scrollToBottom, 100);
                updateChatLocallyRef.current?.(id, response, false);
            }
            pendingMessagesRef.current = pendingMessagesRef.current.filter(p => p.tempId !== tempId);
            setReplyToMessage(null);
        } catch (err) {
            setMessages(prev => prev.filter(m => m.id !== tempId));
            pendingMessagesRef.current = pendingMessagesRef.current.filter(p => p.tempId !== tempId);
            console.error('[ChatWindow] Ошибка отправки стикера:', err);
            setError(err.message || 'Ошибка при отправке стикера');
        } finally {
            setSending(false);
        }
    }, [currentUser, id, currentProfileId, replyToMessage, setMessages, pendingMessagesRef, scrollToBottom, setError, setReplyToMessage]);

    /**
     * Отправка GIF — загружает готовый fileId как вложение.
     * @param {{ fileId: string, url: string, gifId: string, title: string }} gifData
     */
    const handleSendGif = useCallback(async (gifData) => {
        if (!gifData || !gifData.fileId || !currentUser || !id) return;

        setSending(true);

        const tempId = `optimistic-gif-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        const now = new Date().toISOString();
        const displayName = currentUser?.profiles?.[0]?.displayName
            || currentUser?.displayName
            || currentUser?.username
            || 'Я';
        const avatar = currentUser?.profiles?.[0]?.avatar
            ? `/api/files/${currentUser.profiles[0].avatar}`
            : '/default-avatar.png';

        const optimistic = {
            id: tempId,
            content: '',
            text: '',
            type: 'GIF',
            gifId: gifData.gifId,
            createdAt: now,
            sentAt: now,
            sender: { id: currentProfileId, displayName, avatar },
            senderId: currentProfileId,
            senderDisplayName: displayName,
            attachments: [{ fileId: gifData.fileId, type: 'IMAGE', fileName: gifData.title || 'GIF', mimeType: 'image/gif' }],
            attachmentIds: [gifData.fileId],
            invitationAttachments: [],
            reactions: [],
            isDeleted: false,
            _pending: true,
            replyToMessageId: replyToMessage?.id || null,
        };

        pendingMessagesRef.current.push({ tempId, content: '' });
        setMessages(prev => [...prev, optimistic].sort((a, b) =>
            new Date(a.createdAt || a.sentAt) - new Date(b.createdAt || b.sentAt)
        ));
        setTimeout(scrollToBottom, 50);

        try {
            const messagePayload = {
                senderId: currentProfileId || null,
                chatId: id,
                content: '',
                attachmentIds: [gifData.fileId],
                type: 'GIF',
            };
            if (replyToMessage?.id) {
                messagePayload.replyToMessageId = replyToMessage.id;
            }

            const response = await chatsApi.sendMessage(id, messagePayload);

            if (response) {
                setMessages(prev => {
                    const withoutPending = prev.filter(m => m.id !== tempId);
                    const exists = withoutPending.find(m => m.id === response.id);
                    if (exists) {
                        return withoutPending.map(m => m.id === response.id ? { ...m, ...response } : m);
                    }
                    return [...withoutPending, response].sort((a, b) =>
                        new Date(a.createdAt || a.sentAt) - new Date(b.createdAt || b.sentAt)
                    );
                });
                setTimeout(scrollToBottom, 100);
                updateChatLocallyRef.current?.(id, response, false);
            }
            pendingMessagesRef.current = pendingMessagesRef.current.filter(p => p.tempId !== tempId);
            setReplyToMessage(null);
        } catch (err) {
            setMessages(prev => prev.filter(m => m.id !== tempId));
            pendingMessagesRef.current = pendingMessagesRef.current.filter(p => p.tempId !== tempId);
            console.error('[ChatWindow] Ошибка отправки GIF:', err);
            setError(err.message || 'Ошибка при отправке GIF');
        } finally {
            setSending(false);
        }
    }, [currentUser, id, currentProfileId, replyToMessage, setMessages, pendingMessagesRef, scrollToBottom, setError, setReplyToMessage]);

    /**
     * Создание опроса в чате.
     * Сначала отправляет сообщение с вопросом опроса как content,
     * затем создаёт опрос через pollsApi.create с привязкой к этому сообщению.
     * @param {Object} pollData - Данные опроса от CreatePollModal (PollResponse)
     */
    const handleCreatePoll = useCallback(async (pollData) => {
        if (!pollData || !currentUser || !id) return;

        setSending(true);

        const tempId = `optimistic-poll-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        const now = new Date().toISOString();
        const displayName = currentUser?.profiles?.[0]?.displayName
            || currentUser?.displayName
            || currentUser?.username
            || 'Я';
        const avatar = currentUser?.profiles?.[0]?.avatar
            ? `/api/files/${currentUser.profiles[0].avatar}`
            : '/default-avatar.png';

        // Оптимистичное сообщение-опрос
        const optimistic = {
            id: tempId,
            content: pollData.question,
            text: pollData.question,
            type: 'POLL',
            pollId: pollData.id,
            poll: pollData,
            chatId: id,
            createdAt: now,
            sentAt: now,
            sender: { id: currentProfileId, displayName, avatar },
            senderId: currentProfileId,
            senderDisplayName: displayName,
            attachments: [],
            invitationAttachments: [],
            reactions: [],
            isDeleted: false,
            _pending: true,
            replyToMessageId: replyToMessage?.id || null,
        };

        pendingMessagesRef.current.push({ tempId, content: pollData.question });
        setMessages(prev => [...prev, optimistic].sort((a, b) =>
            new Date(a.createdAt || a.sentAt) - new Date(b.createdAt || b.sentAt)
        ));
        setTimeout(scrollToBottom, 50);

        try {
            // Отправляем сообщение с вопросом опроса как content
            const messagePayload = {
                senderId: currentProfileId || null,
                chatId: id,
                content: pollData.question,
            };
            if (replyToMessage?.id) {
                messagePayload.replyToMessageId = replyToMessage.id;
            }

            const response = await chatsApi.sendMessage(id, messagePayload);

            if (response) {
                // Привязываем опрос к созданному сообщению
                try {
                    const updatedPoll = await pollsApi.create(id, {
                        question: pollData.question,
                        multipleChoice: pollData.multipleChoice,
                        options: (pollData.options || []).map(opt => ({ text: opt.text })),
                        messageId: response.id,
                    });

                    // Добавляем опрос в кэш, чтобы матчинг работал для новых сообщений
                    pollsCacheRef.current = [...pollsCacheRef.current, updatedPoll];
                    setPollsVersion(v => v + 1);

                    // Заменяем optimistic на реальное сообщение с привязанным опросом
                    setMessages(prev => {
                        const withoutPending = prev.filter(m => m.id !== tempId);
                        const realMsg = {
                            ...response,
                            type: 'POLL',
                            pollId: updatedPoll.id,
                            poll: updatedPoll,
                        };
                        const exists = withoutPending.find(m => m.id === response.id);
                        if (exists) {
                            return withoutPending.map(m => m.id === response.id ? { ...m, ...realMsg } : m);
                        }
                        return [...withoutPending, realMsg].sort((a, b) =>
                            new Date(a.createdAt || a.sentAt) - new Date(b.createdAt || b.sentAt)
                        );
                    });
                } catch (pollErr) {
                    // Опрос не создался — показываем сообщение-ошибку
                    console.error('[ChatWindow] Failed to create poll:', pollErr);
                    setMessages(prev => prev.filter(m => m.id !== tempId));
                    setError(pollErr.response?.data?.message || 'Не удалось создать опрос');
                }
                setTimeout(scrollToBottom, 100);
                updateChatLocallyRef.current?.(id, response, false);
            }
            pendingMessagesRef.current = pendingMessagesRef.current.filter(p => p.tempId !== tempId);
            setReplyToMessage(null);
        } catch (err) {
            setMessages(prev => prev.filter(m => m.id !== tempId));
            pendingMessagesRef.current = pendingMessagesRef.current.filter(p => p.tempId !== tempId);
            console.error('[ChatWindow] Ошибка отправки опроса:', err);
            setError(err.message || 'Ошибка при отправке опроса');
        } finally {
            setSending(false);
        }
    }, [currentUser, id, currentProfileId, replyToMessage, setMessages, pendingMessagesRef, scrollToBottom, setError, setReplyToMessage]);

    const handleReply = useCallback((message) => {
        setReplyToMessage(message);
        setEditingMessage(null);
    }, []);

    const handleEdit = useCallback((message) => {
        setEditingMessage(message);
        setReplyToMessage(null);
    }, []);

    const handleCancelReply = useCallback(() => {
        setReplyToMessage(null);
    }, []);

    const handleCancelEdit = useCallback(() => {
        setEditingMessage(null);
    }, []);

    const handleDeleteSingle = useCallback(async (message) => {
        if (!message?.id) return;
        try {
            await chatsApi.deleteMessage(message.id);
            const deletedMsg = { ...message, isDeleted: true, content: null };
            setMessages(prev => {
                const updated = prev.map(m => m.id === message.id ? deletedMsg : m);
                // Если удалено последнее сообщение — обновляем превью чата
                // Используем functional update, чтобы не зависеть от stale closure
                const lastMsg = updated[updated.length - 1];
                if (lastMsg && lastMsg.id === message.id) {
                    updateChatLocallyRef.current?.(id, deletedMsg, false);
                }
                return updated;
            });
        } catch (err) {
            console.error('[ChatWindow] Failed to delete message:', err);
            setError(err.message || 'Ошибка при удалении сообщения');
        }
    }, [setMessages, setError, id]);

    const handleMessageReact = useCallback(async (messageId, emoji, isRemoving) => {
        try {
            if (isRemoving) {
                await chatsApi.removeReaction(messageId, emoji);
                setMessages(prev => prev.map(m => {
                    if (m.id !== messageId) return m;
                    return {
                        ...m,
                        reactions: (m.reactions || []).filter(r => !(r.emoji === emoji && (r.userId === currentProfileId || r.profileId === currentProfileId)))
                    };
                }));
            } else {
                // Один пользователь = одна реакция.
                // Читаем актуальные сообщения через ref (синхронно, без гонки).
                const currentMsg = messagesRef.current.find(m => m.id === messageId);
                const myExisting = findMyReaction(currentMsg?.reactions, currentProfileId);
                const oldEmoji = myExisting && myExisting.emoji !== emoji ? myExisting.emoji : null;

                // Если уже есть реакция с другим эмодзи — удаляем её через API.
                if (oldEmoji) {
                    try {
                        await chatsApi.removeReaction(messageId, oldEmoji);
                    } catch {
                        // Сервер мог уже заменить автоматически — игнорируем
                    }
                }

                const reaction = await chatsApi.addReaction(messageId, emoji);
                setMessages(prev => prev.map(m => {
                    if (m.id !== messageId) return m;
                    // Удаляем ВСЕ существующие реакции пользователя (любой эмодзи),
                    // затем добавляем новую — гарантируем одну реакцию на пользователя
                    const filtered = (m.reactions || []).filter(
                        r => !(r.userId === currentProfileId || r.profileId === currentProfileId)
                    );
                    return { ...m, reactions: [...filtered, reaction] };
                }));
            }
        } catch (err) {
            console.error('[ChatWindow] Reaction error:', err);
        }
    }, [currentProfileId, setMessages]);

    // === Рендер ===

    // Аватар для заголовка чата — хук должен быть до ранних возвратов
    const headerAvatarFileId = chat?.chatType === ChatType.DIRECT
        ? (otherProfileInfo?.avatar || null)
        : (chat?.avatar || null);
    const headerAvatarBlobUrl = useAvatarUrl(headerAvatarFileId);

    if (loading) {
        return (
            <HrnbContainer className="chat-window">
                <div className="chat-window__loading">
                    <HrnbLoader />
                </div>
            </HrnbContainer>
        );
    }

    if (error || !chat) {
        return (
            <HrnbContainer className="chat-window">
                <HrnbAlert variant="danger" onClose={() => navigate('/chats')}>
                    {error || 'Чат не найден'}
                </HrnbAlert>
            </HrnbContainer>
        );
    }

    // Для DIRECT чатов показываем имя собеседника, для GROUP — заголовок чата
    const chatTitle = chat.chatType === ChatType.DIRECT
        ? (otherProfileInfo?.displayName || 'Личный чат')
        : (chat.title || chat.name || 'Групповой чат');

    // Аватар для заголовка чата (DIRECT — аватар собеседника через blob URL)
    const headerAvatar = headerAvatarBlobUrl
        || (chat?.chatType === ChatType.DIRECT
            ? (otherProfileInfo?.avatarUrl || '/default-avatar.png')
            : null);

    return (
        <HrnbContainer className="chat-window">
            {/* Компактный заголовок */}
            <div className="chat-window__header-bar">
                {!selectionMode ? (
                    <>
                        <div className="chat-window__header-info">
                            <HrnbButton
                                variant="secondary"
                                size="sm"
                                className="chat-window__back-btn"
                                onClick={() => navigate('/chats')}
                            >
                                ←
                            </HrnbButton>
                            {headerAvatar && (
                                <HrnbImage
                                    src={headerAvatar}
                                    alt={chatTitle}
                                    size={32}
                                    rounded={true}
                                    className="chat-window__header-avatar chat-window__header-avatar-link"
                                    onClick={() => {
                                        if (chat.chatType === ChatType.DIRECT && otherProfileInfo?.id) {
                                            navigate(`/profile/${otherProfileInfo.id}`);
                                        } else if (chat.battleId) {
                                            navigate(`/battles/${chat.battleId}`);
                                        }
                                    }}
                                />
                            )}
                            {chat.chatType !== ChatType.DIRECT && !headerAvatar && (
                                <span
                                    className="chat-window__header-icon chat-window__header-icon-link"
                                    onClick={() => {
                                        if (chat.battleId) navigate(`/battles/${chat.battleId}`);
                                    }}
                                >
                                    {chat.chatType === ChatType.BATTLE_SUPPORT ? '⚔' : '👥'}
                                </span>
                            )}
                            <span className="chat-window__title-text">{chatTitle}</span>
                            {chat.chatType === ChatType.DIRECT && (
                                <OnlineStatusIndicator
                                    isOnline={otherParticipantPresence.isOnline}
                                    lastActivity={otherParticipantPresence.lastActivity}
                                    size="sm"
                                    showLabel={true}
                                />
                            )}
                            <span className="chat-window__status-dot">
                                {wsConnected ? '🟢' : '🟡'}
                            </span>
                        </div>
                        <div className="chat-window__header-actions">
                            <HrnbButton
                                variant="light"
                                size="sm"
                                onClick={handleToggleMute}
                                title={isMuted ? 'Включить звук' : 'Отключить звук'}
                                className={isMuted ? 'chat-window__mute-btn--active' : ''}
                            >
                                {isMuted ? '🔇' : '🔔'}
                            </HrnbButton>
                            <HrnbButton
                                variant="light"
                                size="sm"
                                onClick={searchMode ? handleExitSearch : () => setSearchMode(true)}
                                disabled={selectionMode}
                                title="Поиск по сообщениям"
                            >
                                🔍
                            </HrnbButton>
                            <HrnbButton
                                variant="light"
                                size="sm"
                                onClick={() => setShowBookmarksPanel(true)}
                                disabled={selectionMode}
                                title="Закладки"
                            >
                                🔖
                            </HrnbButton>
                            {chat.chatType !== ChatType.DIRECT && (
                                <HrnbButton
                                    variant="light"
                                    size="sm"
                                    onClick={() => setShowManageModal(true)}
                                    disabled={selectionMode}
                                    title="Управление чатом"
                                >
                                    ⚙
                                </HrnbButton>
                            )}
                            <HrnbButton
                                variant="light"
                                size="sm"
                                onClick={() => setSelectionMode(true)}
                                icon={
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                                         stroke="currentColor" strokeWidth="2" strokeLinecap="round"
                                         strokeLinejoin="round">
                                        <path d="M9 11l3 3L22 4" />
                                        <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
                                    </svg>
                                }
                            >
                                Выбрать
                            </HrnbButton>
                        </div>
                    </>
                ) : (
                    <>
                        <div className="chat-window__header-info">
                            <HrnbButton variant="secondary" size="sm" onClick={exitSelectionMode}>
                                ✕
                            </HrnbButton>
                            <span className="chat-window__selection-count">
                                {selectedMessageIds.size > 0
                                    ? `Выбрано: ${selectedMessageIds.size}`
                                    : 'Выбор сообщений'}
                            </span>
                        </div>
                        <div className="chat-window__header-actions">
                            <HrnbButton
                                variant="light"
                                size="sm"
                                onClick={toggleSelectAll}
                                disabled={messages.length === 0}
                            >
                                {allSelected ? 'Снять все' : 'Все'}
                            </HrnbButton>
                        </div>
                    </>
                )}
            </div>

            {/* Панель поиска по сообщениям */}
            {searchMode && !selectionMode && (
                <div className="chat-window__search-bar">
                    <input
                        type="search"
                        className="chat-window__search-input"
                        placeholder="Поиск по сообщениям..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        autoFocus
                    />
                    {searchQuery.trim() && (
                        <span className="chat-window__search-count">
                            {searchResults.length > 0
                                ? `${currentSearchIndex + 1} / ${searchResults.length}`
                                : 'Не найдено'}
                        </span>
                    )}
                    <button
                        className="chat-window__search-nav-btn"
                        onClick={handleSearchPrev}
                        disabled={searchResults.length === 0}
                        title="Предыдущее (Shift+Enter)"
                    >
                        ↑
                    </button>
                    <button
                        className="chat-window__search-nav-btn"
                        onClick={handleSearchNext}
                        disabled={searchResults.length === 0}
                        title="Следующее (Enter)"
                    >
                        ↓
                    </button>
                    {hasMoreMessages && (
                        <button
                            className="chat-window__search-load-all"
                            onClick={loadAllMessagesForSearch}
                            disabled={loadingAllForSearch}
                            title="Загрузить всю историю для полного поиска"
                        >
                            {loadingAllForSearch ? 'Загрузка...' : 'Вся история'}
                        </button>
                    )}
                    <button
                        className="chat-window__search-close"
                        onClick={handleExitSearch}
                        title="Закрыть поиск (Esc)"
                    >
                        ✕
                    </button>
                </div>
            )}

            {/* Закреплённые сообщения */}
            {pinnedMessages.length > 0 && (
                <div className="chat-window__pinned-container">
                    {pinnedMessages.map(msg => (
                        <PinnedMessageBanner
                            key={msg.id}
                            message={msg}
                            onUnpin={handleUnpinMessage}
                            onScrollTo={() => scrollToPinnedMessage(msg.id)}
                        />
                    ))}
                </div>
            )}

            {/* Сообщения + плавающая кнопка */}
            <div className="chat-window__messages-area">
                <div className="chat-window__messages" ref={messagesContainerRef} onScroll={handleScroll}>
                {hasMoreMessages && messages.length > 0 && (
                    <div ref={topSentinelRef} className="chat-window__load-more-sentinel">
                        {loadingMore && <HrnbLoader />}
                    </div>
                )}
                {messages.length === 0 ? (
                    <div className="chat-window__no-messages">
                        <p>Это начало чата. Напишите первое сообщение!</p>
                    </div>
                ) : (
                    <div
                        style={{
                            height: `${virtualizer.getTotalSize()}px`,
                            position: 'relative',
                            width: '100%',
                        }}
                    >
                        {virtualizer.getVirtualItems().map(virtualItem => {
                            const item = items[virtualItem.index];
                            if (!item) return null;

                            const itemStyle = {
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                transform: `translateY(${virtualItem.start}px)`,
                            };

                            if (item.type === 'date-separator') {
                                return (
                                    <div
                                        key={item.key}
                                        ref={virtualizer.measureElement}
                                        data-index={virtualItem.index}
                                        style={itemStyle}
                                    >
                                        <div className="chat-window__date-separator">
                                            <span className="chat-window__date-separator-text">
                                                {formatDateSeparator(item.date)}
                                            </span>
                                        </div>
                                    </div>
                                );
                            }

                            if (item.type === 'unread-separator') {
                                return (
                                    <div
                                        key={item.key}
                                        ref={virtualizer.measureElement}
                                        data-index={virtualItem.index}
                                        style={itemStyle}
                                    >
                                        <div className="chat-window__unread-separator">
                                            <span className="chat-window__unread-separator-text">
                                                Непрочитанные сообщения
                                            </span>
                                        </div>
                                    </div>
                                );
                            }

                            // type === 'message'
                            return (
                                <div
                                    key={item.key}
                                    ref={virtualizer.measureElement}
                                    data-index={virtualItem.index}
                                    data-message-id={item.message.id}
                                    className={`chat-window__message-wrap ${item.searchClass}`}
                                    style={itemStyle}
                                >
                                    <ChatMessage
                                        message={item.message}
                                        isOwn={item.isOwn}
                                        chatType={chat.chatType}
                                        chatId={id}
                                        isGrouped={item.isGrouped}
                                        isGroupedLast={item.isGroupedLast}
                                        selectionMode={selectionMode}
                                        isSelected={selectedMessageIds.has(item.message.id)}
                                        onSelect={toggleMessageSelection}
                                        onLongPress={enterSelectionMode}
                                        currentProfileId={currentProfileId}
                                        chatParticipants={participantProfiles}
                                        replyMessage={item.replyMsg}
                                        onReply={handleReply}
                                        onEdit={handleEdit}
                                        onDelete={handleDeleteSingle}
                                        onReact={(emoji, isRemoving) => handleMessageReact(item.message.id, emoji, isRemoving)}
                                        onForward={handleForwardSingle}
                                        onPin={handlePinMessage}
                                        onUnpin={handleUnpinMessage}
                                        onBookmark={handleToggleBookmark}
                                        isBookmarked={bookmarkedMessageIds.has(item.message.id)}
                                        onInviteAction={(action, inviteId) => {
                                            const actionToStatus = {
                                                accept: 'ACCEPTED',
                                                decline: 'DECLINED',
                                                revoke: 'REVOKED'
                                            };
                                            const newStatus = actionToStatus[action];
                                            setMessages(prev => prev.map(msg => {
                                                const hasInvite = (msg.invitationAttachments || []).some(
                                                    att => att.inviteId === inviteId
                                                );
                                                if (hasInvite) {
                                                    return {
                                                        ...msg,
                                                        invitationAttachments: (msg.invitationAttachments || []).map(att =>
                                                            att.inviteId === inviteId
                                                                ? { ...att, status: newStatus }
                                                                : att
                                                        )
                                                    };
                                                }
                                                return msg;
                                            }));
                                        }}
                                    />
                                </div>
                            );
                        })}
                    </div>
                )}
                <div ref={messagesEndRef} />
                </div>

                {/* Плавающая кнопка «прокрутить вниз» */}
                {showScrollButton && (
                    <div className="chat-window__scroll-bottom-btn" onClick={handleScrollToBottomClick}>
                        {newMessagesCount > 0 && (
                            <span className="chat-window__scroll-bottom-badge">
                                {newMessagesCount > 99 ? '99+' : newMessagesCount}
                            </span>
                        )}
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                             strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="6 9 12 15 18 9" />
                        </svg>
                    </div>
                )}
            </div>

            {/* Индикатор набора текста */}
            {typingUsers.size > 0 && (
                <div className="chat-window__typing">
                    <span className="chat-window__typing-text">
                        {Array.from(typingUsers).join(', ')} {typingUsers.size === 1 ? 'печатает' : 'печатают'}
                    </span>
                    <span className="chat-window__typing-dots">
                        <span className="chat-window__typing-dot" />
                        <span className="chat-window__typing-dot" />
                        <span className="chat-window__typing-dot" />
                    </span>
                </div>
            )}

            {/* Форма отправки */}
            {!selectionMode && (
                <SendMessageForm
                    chatId={id}
                    onSend={handleSendMessage}
                    onSendSticker={handleSendSticker}
                    onSendGif={handleSendGif}
                    participants={participantProfiles}
                    onTyping={() => {
                        if (wsConnected && currentProfileId) {
                            const displayName = currentUser?.profiles?.[0]?.displayName || currentUser?.displayName || currentUser?.username || 'Аноним';
                            sendTypingWs(currentProfileId, displayName);
                        }
                    }}
                    onStopTyping={() => {
                        if (wsConnected && currentProfileId) {
                            const displayName = currentUser?.profiles?.[0]?.displayName || currentUser?.displayName || currentUser?.username || 'Аноним';
                            sendStopTypingWs(currentProfileId, displayName);
                        }
                    }}
                    disabled={sending}
                    replyToMessage={replyToMessage}
                    editingMessage={editingMessage}
                    onCancelReply={handleCancelReply}
                    onCancelEdit={handleCancelEdit}
                    showPollButton={chat?.chatType !== ChatType.DIRECT}
                    onCreatePoll={handleCreatePoll}
                />
            )}

            {showForwardModal && (
                <ForwardMessageModal
                    currentChatId={id}
                    onClose={() => { setShowForwardModal(false); setSingleForwardMessage(null); }}
                    onForward={handleForwardSelected}
                    loading={actionLoading}
                />
            )}

            {showManageModal && chat.chatType !== ChatType.DIRECT && (
                <ManageChatModal
                    open={showManageModal}
                    onClose={() => setShowManageModal(false)}
                    chat={chat}
                    currentProfileId={currentProfileId}
                    onChatUpdated={handleChatUpdated}
                />
            )}

            {showBookmarksPanel && (
                <div className="chat-window__bookmarks-overlay" onClick={() => setShowBookmarksPanel(false)}>
                    <div
                        className="chat-window__bookmarks-modal"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <BookmarksPanel
                            onNavigate={(targetChatId, targetMessageId) => {
                                setShowBookmarksPanel(false);
                                if (targetChatId !== id) {
                                    navigate(`/chats/${targetChatId}?msg=${targetMessageId}`);
                                } else {
                                    // Тот же чат — скроллим к сообщению
                                    const container = messagesContainerRef.current;
                                    if (container) {
                                        const el = container.querySelector(`[data-message-id="${targetMessageId}"]`);
                                        if (el) {
                                            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                            el.classList.add('chat-message--pinned-highlight');
                                            setTimeout(() => el.classList.remove('chat-message--pinned-highlight'), 2000);
                                        }
                                    }
                                }
                            }}
                            onClose={() => setShowBookmarksPanel(false)}
                        />
                    </div>
                </div>
            )}

            {/* Плавающая панель действий в режиме выбора (как в Telegram) */}
            {selectionMode && (
                <div className="chat-window__selection-bar">
                    <button
                        className="chat-window__selection-action"
                        onClick={handleCopySelected}
                        disabled={selectedMessageIds.size === 0}
                        title="Копировать"
                    >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                             strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                        </svg>
                        <span>Копировать</span>
                    </button>
                    <button
                        className="chat-window__selection-action"
                        onClick={() => setShowForwardModal(true)}
                        disabled={selectedMessageIds.size === 0 || actionLoading}
                        title="Переслать"
                    >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                             strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="15 17 20 12 15 7" />
                            <path d="M4 18v-2a4 4 0 0 1 4-4h12" />
                        </svg>
                        <span>Переслать</span>
                    </button>
                    <button
                        className="chat-window__selection-action chat-window__selection-action--danger"
                        onClick={handleDeleteSelected}
                        disabled={selectedMessageIds.size === 0 || actionLoading}
                        title="Удалить"
                    >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                             strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="3 6 5 6 21 6" />
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                        </svg>
                        <span>Удалить</span>
                    </button>
                </div>
            )}
        </HrnbContainer>
    );
}
